import { extractImageFrames, clampTo } from '../../tools';
import type { baiduModelConfig } from '../types';


export const googleConfigs: Record<string, baiduModelConfig> = {
    'veo3.1-lite': {
        // https://cloud.baidu.com/doc/VOD/s/mm3h9frta#texttovideove31ltaskinput
        // https://cloud.baidu.com/doc/VOD/s/mm3h9frta#imagetovideove31ltaskinput
        apiPath: (params) => {
            const { frameMode } = params;
            if (frameMode === 'first' || frameMode === 'first_last') {
                return '/v3/aigc/image_to_video';
            } else {
                return '/v3/aigc/text_to_video';
            }
        },
        pollApiPath: '/v3/tasks/{id}',
        statusMapping: {
            taskIdPath: 'taskId',
            success: ['SUCCESS'],
            failed: ['FAILED'],
            processing: ['RUNNING'],
            statePath: 'status',
            videoUrlPath: 'videoGenerateTaskInfo.videoGenerateTaskOutput.mediaBasicInfos[0].source.sourceUrl',
            errorPath: 'videoGenerateTaskInfo.errMsg',
        },
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    model: 'VE3.1L',
                    modelVE31LTaskInput: {
                        prompt: prompt.slice(0, 2000),
                        negativePrompt: typeof providerOptions?.negativePrompt === 'string' ? providerOptions.negativePrompt : undefined,
                        aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                        durationSeconds: clampTo(duration || 8, [4, 6, 8] as const, 8),
                        resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                        seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                        // 首帧 URL
                        image: firstImage,
                        // 可选参数
                        generateAudio: typeof providerOptions?.generateAudio === 'boolean' ? providerOptions.generateAudio : false,
                        // system
                        n: 1,
                        personGeneration: 'allow_adult',
                    }
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    model: 'VE3.1L',
                    modelVE31LTaskInput: {
                        prompt: prompt.slice(0, 2000),
                        negativePrompt: typeof providerOptions?.negativePrompt === 'string' ? providerOptions.negativePrompt : undefined,
                        aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                        durationSeconds: clampTo(duration || 8, [4, 6, 8] as const, 8),
                        resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                        seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                        // 首帧 URL
                        image: firstImage,
                        // 尾帧 URL
                        lastFrame: lastImage,
                        // 可选参数
                        generateAudio: typeof providerOptions?.generateAudio === 'boolean' ? providerOptions.generateAudio : false,
                        // system
                        n: 1,
                        personGeneration: 'allow_adult',
                    }
                }
            // 文生视频模式
            } else {
                return {
                    model: 'VE3.1L',
                    modelVE31LTaskInput: {
                        prompt: prompt.slice(0, 2000),
                        negativePrompt: typeof providerOptions?.negativePrompt === 'string' ? providerOptions.negativePrompt : undefined,
                        aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                        durationSeconds: clampTo(duration || 8, [4, 6, 8] as const, 8),
                        resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                        seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                        // 可选参数
                        generateAudio: typeof providerOptions?.generateAudio === 'boolean' ? providerOptions.generateAudio : false,
                        // system
                        n: 1,
                        personGeneration: 'allow_adult',
                    }
                }
            }
        } 
    },
    'veo3.1-fast': {
        // https://cloud.baidu.com/doc/VOD/s/mm3h9frta#texttovideove31ltaskinput
        // https://cloud.baidu.com/doc/VOD/s/mm3h9frta#imagetovideove31ltaskinput
        apiPath: (params) => {
            const { frameMode } = params;
            if (frameMode === 'first' || frameMode === 'first_last' || frameMode === 'ref') {
                return '/v3/aigc/image_to_video';
            } else {
                return '/v3/aigc/text_to_video';
            }
        },
        pollApiPath: '/v3/tasks/{id}',
        statusMapping: {
            taskIdPath: 'taskId',
            success: ['SUCCESS'],
            failed: ['FAILED'],
            processing: ['RUNNING'],
            statePath: 'status',
            videoUrlPath: 'videoGenerateTaskInfo.videoGenerateTaskOutput.mediaBasicInfos[0].source.sourceUrl',
            errorPath: 'videoGenerateTaskInfo.errMsg',
        },
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            if (frameMode === 'first') {
                return {
                    model: 'VE3.1F',
                    modelVE31FTaskInput: {
                        prompt: prompt.slice(0, 2000),
                        negativePrompt: typeof providerOptions?.negativePrompt === 'string' ? providerOptions.negativePrompt : undefined,
                        aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                        durationSeconds: clampTo(duration || 8, [4, 6, 8] as const, 8),
                        resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                        seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                        // 首帧 URL
                        image: firstImage,
                        // 可选参数
                        generateAudio: typeof providerOptions?.generateAudio === 'boolean' ? providerOptions.generateAudio : false,
                        // system
                        n: 1,
                        personGeneration: 'allow_adult',
                    }
                };
            } else if (frameMode === 'first_last') {
                return {
                    model: 'VE3.1F',
                    modelVE31FTaskInput: {
                        prompt: prompt.slice(0, 2000),
                        negativePrompt: typeof providerOptions?.negativePrompt === 'string' ? providerOptions.negativePrompt : undefined,
                        aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                        durationSeconds: clampTo(duration || 8, [4, 6, 8] as const, 8),
                        resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                        seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                        // 首帧 URL
                        image: firstImage,
                        // 尾帧 URL
                        lastFrame: lastImage,
                        // 可选参数
                        generateAudio: typeof providerOptions?.generateAudio === 'boolean' ? providerOptions.generateAudio : false,
                        // system
                        n: 1,
                        personGeneration: 'allow_adult',
                    }
                }
            } else if (frameMode === 'ref') {
                return {
                    model: 'VE3.1F',
                    modelVE31FTaskInput: {
                        prompt: prompt.slice(0, 2000),
                        negativePrompt: typeof providerOptions?.negativePrompt === 'string' ? providerOptions.negativePrompt : undefined,
                        aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                        durationSeconds: clampTo(duration || 8, [4, 6, 8] as const, 8),
                        resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                        seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                        // 参考帧 URL
                        referenceImages: imagePrompt!.slice(0, 3),
                        // 可选参数
                        generateAudio: typeof providerOptions?.generateAudio === 'boolean' ? providerOptions.generateAudio : false,
                        // system
                        n: 1,
                        personGeneration: 'allow_adult',
                    }
                }
            // 文生视频模式
            } else {
                return {
                    model: 'VE3.1F',
                    modelVE31FTaskInput: {
                        prompt: prompt.slice(0, 2000),
                        negativePrompt: typeof providerOptions?.negativePrompt === 'string' ? providerOptions.negativePrompt : undefined,
                        aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                        durationSeconds: clampTo(duration || 8, [4, 6, 8] as const, 8),
                        resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                        seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                        // 可选参数
                        generateAudio: typeof providerOptions?.generateAudio === 'boolean' ? providerOptions.generateAudio : false,
                        // system
                        n: 1,
                        personGeneration: 'allow_adult',
                    }
                }
            }
        } 
    },
    'veo3.1': {
        // https://cloud.baidu.com/doc/VOD/s/mm3h9frta#texttovideove31taskinput
        // https://cloud.baidu.com/doc/VOD/s/mm3h9frta#imagetovideove31taskinput
        apiPath: (params) => {
            const { frameMode } = params;
            if (frameMode === 'first' || frameMode === 'first_last' || frameMode === 'ref') {
                return '/v3/aigc/image_to_video';
            } else {
                return '/v3/aigc/text_to_video';
            }
        },
        pollApiPath: '/v3/tasks/{id}',
        enableTranslation: false,
        statusMapping: {
            taskIdPath: 'taskId',
            success: ['SUCCESS'],
            failed: ['FAILED'],
            processing: ['RUNNING'],
            statePath: 'status',
            videoUrlPath: 'videoGenerateTaskInfo.videoGenerateTaskOutput.mediaBasicInfos[0].source.sourceUrl',
            errorPath: 'videoGenerateTaskInfo.errMsg',
        },
        buildInput: (params) => {
            const { prompt, imagePrompt, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    model: 'VE3.1',
                    modelVE31TaskInput: {
                        prompt: prompt.slice(0, 2000),
                        negativePrompt: typeof providerOptions?.negativePrompt === 'string' ? providerOptions.negativePrompt : undefined,
                        aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                        durationSeconds: clampTo(duration || 8, [4, 6, 8] as const, 8),
                        resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                        seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                        // 首帧
                        image: firstImage,
                        // 可选参数
                        generateAudio: typeof providerOptions?.generateAudio === 'boolean' ? providerOptions.generateAudio : false,
                        // system
                        n: 1,
                        personGeneration: 'allow_adult',
                    }
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    model: 'VE3.1',
                    modelVE31TaskInput: {
                        prompt: prompt.slice(0, 2000),
                        negativePrompt: typeof providerOptions?.negativePrompt === 'string' ? providerOptions.negativePrompt : undefined,
                        aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                        durationSeconds: clampTo(duration || 8, [4, 6, 8] as const, 8),
                        resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                        seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                        // 首帧
                        image: firstImage,
                        // 尾帧
                        lastFrame: lastImage,
                        // 可选参数
                        generateAudio: typeof providerOptions?.generateAudio === 'boolean' ? providerOptions.generateAudio : false,
                        // system
                        n: 1,
                        personGeneration: 'allow_adult',
                    }
                }
            // 参考模式
            } else if (frameMode === 'ref') {
                return {
                    model: 'VE3.1',
                    modelVE31TaskInput: {
                        prompt: prompt.slice(0, 2000),
                        negativePrompt: typeof providerOptions?.negativePrompt === 'string' ? providerOptions.negativePrompt : undefined,
                        aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                        durationSeconds: clampTo(duration || 8, [4, 6, 8] as const, 8),
                        resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                        seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                        // 参考帧 URL
                        referenceImages: imagePrompt!.slice(0, 3),
                        // 可选参数
                        generateAudio: typeof providerOptions?.generateAudio === 'boolean' ? providerOptions.generateAudio : false,
                        // system
                        n: 1,
                        personGeneration: 'allow_adult',
                    }
                }
            // 文生视频模式
            } else {
                return {
                    model: 'VE3.1',
                    modelVE31TaskInput: {
                        prompt: prompt.slice(0, 2000),
                        negativePrompt: typeof providerOptions?.negativePrompt === 'string' ? providerOptions.negativePrompt : undefined,
                        aspectRatio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                        durationSeconds: clampTo(duration || 8, [4, 6, 8] as const, 8),
                        resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                        seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                        // 可选参数
                        generateAudio: typeof providerOptions?.generateAudio === 'boolean' ? providerOptions.generateAudio : false,
                        // system
                        n: 1,
                        personGeneration: 'allow_adult',
                    }
                }
            }
        } 
    }
}